// src/components/UploadForm.js
import React, { useState } from "react";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Alert from "@mui/material/Alert";
import Stack from "@mui/material/Stack";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import TextField from "@mui/material/TextField";
import config from "../config";

const API_HOST = config.API_HOST;
const MAX_SIZE = config.MAX_FILE_SIZE; // e.g., 5 * 1024 * 1024

const getExt = (name = "") => name.split(".").pop()?.toLowerCase();

const validateFile = (file, { exts, maxSize }) => {
  if (!file) return { ok: false, msg: "No file selected." };
  const ext = getExt(file.name);
  if (!exts.includes(ext)) {
    return { ok: false, msg: `Invalid file type. Allowed: ${exts.join(", ")}.` };
  }
  if (file.size > maxSize) {
    return { ok: false, msg: `File size should not exceed ${Math.round(maxSize / (1024 * 1024))} MB.` };
  }
  return { ok: true };
};

const UploadForm = ({ onAnalysis }) => {
  const [resumeFile, setResumeFile] = useState(null);

  // JD input mode: "file" (.docx) or "text" (typed)
  const [jdMode, setJdMode] = useState("file");
  const [jdFile, setJdFile] = useState(null);
  const [jdText, setJdText] = useState("");

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleResumeChange = (e) => {
    const file = e.target.files?.[0];
    const check = validateFile(file, { exts: ["docx"], maxSize: MAX_SIZE });
    if (!check.ok) {
      setError(`Resume: ${check.msg}`);
      setResumeFile(null);
    } else {
      setError("");
      setResumeFile(file);
    }
  };

  const handleJdFileChange = (e) => {
    const file = e.target.files?.[0];
    const check = validateFile(file, {
      exts: ["docx"], // JD file mode strictly docx (text mode handles free text)
      maxSize: MAX_SIZE,
    });
    if (!check.ok) {
      setError(`JD: ${check.msg}`);
      setJdFile(null);
    } else {
      setError("");
      setJdFile(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate resume
    if (!resumeFile) return setError("Resume is required.");
    const rCheck = validateFile(resumeFile, { exts: ["docx"], maxSize: MAX_SIZE });
    if (!rCheck.ok) return setError(`Resume: ${rCheck.msg}`);

    const formData = new FormData();
    formData.append("resume", resumeFile);

    // Handle JD based on mode
    if (jdMode === "file") {
      if (!jdFile) return setError("JD (.docx) is required.");
      const jCheck = validateFile(jdFile, { exts: ["docx"], maxSize: MAX_SIZE });
      if (!jCheck.ok) return setError(`JD: ${jCheck.msg}`);
      formData.append("jd", jdFile);
    } else {
      // jdMode === "text"
      const text = jdText.trim();
      if (!text) return setError("Please paste or type the JD text.");
      const textBytes = new TextEncoder().encode(text).length;
      if (textBytes > MAX_SIZE) {
        return setError(`JD text is too large. Limit: ${Math.round(MAX_SIZE / (1024 * 1024))} MB.`);
      }
      // Create a .txt File so backend receives it under the same "jd" field
      const blob = new Blob([text], { type: "text/plain" });
      const file = new File([blob], "jd.txt", { type: "text/plain" });
      formData.append("jd", file);
    }

    try {
      setLoading(true);
      const resp = await fetch(`${API_HOST}/analyze`, {
        method: "POST",
        body: formData,
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        throw new Error(text || `Server responded with ${resp.status}`);
      }
      const data = await resp.json();
      onAnalysis?.(data);
    } catch (err) {
      setError(err?.message || "Something went wrong while uploading.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "50vh",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          gap: 3,
          width: "100%",
          maxWidth: 640,
        }}
      >
        {/* Resume uploader */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "center" }}>
          <Button
            variant="contained"
            component="label"
            sx={{ width: 240 }}
            aria-label="Upload resume (.docx)"
          >
            Upload Resume
            <input
              type="file"
              accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              hidden
              onChange={handleResumeChange}
            />
          </Button>
          {resumeFile && (
            <Typography variant="body2" color="primary">
              {resumeFile.name}
            </Typography>
          )}
        </Box>

        {/* JD mode tabs */}
        <Box sx={{ width: "100%", maxWidth: 640 }}>
          <Tabs
            value={jdMode}
            onChange={(_, v) => {
              setJdMode(v);
              setError("");
            }}
            centered
            aria-label="Job description input mode"
          >
            <Tab label="Upload JD (.docx)" value="file" />
            <Tab label="Paste JD Text" value="text" />
          </Tabs>
        </Box>

        {/* JD - file mode */}
        {jdMode === "file" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, width: "100%", justifyContent: "center" }}>
            <Button
              variant="contained"
              component="label"
              sx={{ width: 240 }}
              aria-label="Upload job description (.docx)"
            >
              Job Description
              <input
                type="file"
                accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                hidden
                onChange={handleJdFileChange}
              />
            </Button>
            {jdFile && (
              <Typography variant="body2" color="primary">
                {jdFile.name}
              </Typography>
            )}
          </Box>
        )}

        {/* JD - text mode */}
        {jdMode === "text" && (
          <TextField
            label="Job Description (paste or type)"
            placeholder="Paste the JD here..."
            value={jdText}
            onChange={(e) => setJdText(e.target.value)}
            multiline
            minRows={6}
            fullWidth
            sx={{ maxWidth: 640 }}
            inputProps={{ maxLength: 50000 }} // soft cap; size check still enforced by bytes
            helperText={`${jdText.length} characters`}
          />
        )}

        {error && (
          <Alert severity="error" sx={{ width: "100%", maxWidth: 640 }}>
            {error}
          </Alert>
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={loading}
          sx={{
            width: 240,
            bgcolor: "success.main",
            "&:hover": { bgcolor: "success.dark" },
            mt: 1,
          }}
        >
          {loading ? "Analyzing..." : "Analyze"}
        </Button>

        <Typography variant="caption" sx={{ color: "text.secondary", mt: -1 }}>
          Max size: {Math.round(MAX_SIZE / (1024 * 1024))} MB • Resume: .docx • JD: .docx or paste text
        </Typography>
      </Box>
    </Box>
  );
};

export default UploadForm;
